import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Plus, Scale, Search } from 'lucide-react';
import {
  Button,
  ConfirmDialog,
  EmptyState,
  Input,
  PageHeader,
  Select,
  Spinner,
  useToast,
  type SelectOption,
} from '../../ui';
import { useDecisions } from '../../features/decisions/useDecisions';
import {
  collectOwners,
  collectServices,
  collectYears,
  filterDecisions,
} from '../../features/decisions/decisionModel';
import type { DecisionFilter, DecisionSummary } from '../../features/decisions/types';
import { DecisionCard } from './DecisionCard';
import { DecisionModal } from './DecisionModal';

const EMPTY_FILTER: DecisionFilter = {
  owner: '',
  service: '',
  goalId: '',
  year: '',
  query: '',
};

/**
 * Decision Log — the durable record of engineering decisions and their WHY.
 * Record a decision, then filter by owner / service / goal / year or search the
 * decision / context / rationale text.
 */
export function DecisionsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    decisions,
    goals,
    workstreams,
    tasks,
    services,
    documents,
    currentAlias,
    goalTitles,
    workstreamTitles,
    storyTitles,
    serviceTitles,
    documentTitles,
    loading,
    error,
    create,
    remove,
  } = useDecisions();

  const [filter, setFilter] = useState<DecisionFilter>(EMPTY_FILTER);
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<DecisionSummary | null>(null);
  const [deleting, setDeleting] = useState(false);

  const ownerOptions: SelectOption[] = useMemo(
    () => collectOwners(decisions).map((v) => ({ value: v, label: v })),
    [decisions],
  );
  const serviceFacetOptions: SelectOption[] = useMemo(
    () => collectServices(decisions).map((v) => ({ value: v, label: serviceTitles.get(v) ?? v })),
    [decisions, serviceTitles],
  );
  const yearOptions: SelectOption[] = useMemo(
    () => collectYears(decisions).map((v) => ({ value: v, label: v })),
    [decisions],
  );
  const goalOptions: SelectOption[] = useMemo(
    () => goals.map((g) => ({ value: g.id, label: g.title || g.id })),
    [goals],
  );

  const filtered = useMemo(() => filterDecisions(decisions, filter), [decisions, filter]);
  const filtersActive =
    !!filter.owner || !!filter.service || !!filter.goalId || !!filter.year || !!filter.query;

  const setField = <K extends keyof DecisionFilter>(key: K, value: DecisionFilter[K]) =>
    setFilter((f) => ({ ...f, [key]: value }));

  const onSubmit = async (draft: Parameters<typeof create>[0]) => {
    await create(draft);
    toast({ tone: 'success', title: 'Decision recorded', description: draft.decision });
  };
  const onDelete = (d: DecisionSummary) => setPendingDelete(d);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await remove(pendingDelete.id);
      toast({ tone: 'success', title: 'Decision removed', description: pendingDelete.decision });
      setPendingDelete(null);
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Could not remove decision',
        description: e instanceof Error ? e.message : 'Delete failed.',
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <PageHeader
        icon={<Scale className="h-5 w-5" />}
        title="Decision Log"
        description="Why we did it this way — decisions, options weighed, and the rationale, kept discoverable."
        actions={
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setModalOpen(true)}>
            Record decision
          </Button>
        }
      />

      <div className="mb-4">
        <Input
          placeholder="Search decision, context, rationale…"
          value={filter.query}
          onChange={(e) => setField('query', e.target.value)}
          className="pl-9"
        />
        <Search className="pointer-events-none -mt-[26px] ml-3 h-4 w-4 text-zinc-500" aria-hidden="true" />
      </div>

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Select label="Owner" placeholder="All owners" value={filter.owner} options={ownerOptions} onChange={(e) => setField('owner', e.target.value)} />
        <Select label="Service" placeholder="All services" value={filter.service} options={serviceFacetOptions} onChange={(e) => setField('service', e.target.value)} />
        <Select label="Goal" placeholder="All goals" value={filter.goalId} options={goalOptions} onChange={(e) => setField('goalId', e.target.value)} />
        <Select label="Year" placeholder="All years" value={filter.year} options={yearOptions} onChange={(e) => setField('year', e.target.value)} />
      </div>

      <div className="mb-4 flex items-center gap-3">
        {filtersActive && (
          <Button variant="ghost" size="sm" onClick={() => setFilter(EMPTY_FILTER)}>
            Clear filters
          </Button>
        )}
        <span className="ml-auto text-xs text-zinc-500">
          {filtered.length} of {decisions.length} {decisions.length === 1 ? 'decision' : 'decisions'}
        </span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Spinner label="Loading decisions" />
        </div>
      ) : error ? (
        <EmptyState icon={<AlertTriangle className="h-6 w-6" />} title="Couldn't load decisions" description={error} />
      ) : decisions.length === 0 ? (
        <EmptyState
          icon={<Scale className="h-6 w-6" />}
          title="No decisions recorded"
          description="Record the first decision so the rationale outlives the meeting it was made in."
          action={
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setModalOpen(true)}>
              Record decision
            </Button>
          }
        />
      ) : filtered.length === 0 ? (
        <EmptyState icon={<Scale className="h-6 w-6" />} title="No decisions match" description="Try clearing filters or broadening your search." />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {filtered.map((d) => (
            <DecisionCard
              key={d.id}
              decision={d}
              goalTitle={d.relatedGoalId ? goalTitles.get(d.relatedGoalId) : undefined}
              workstreamTitle={d.relatedWorkstreamId ? workstreamTitles.get(d.relatedWorkstreamId) : undefined}
              docTitle={d.relatedDocId ? documentTitles.get(d.relatedDocId) : undefined}
              serviceTitles={serviceTitles}
              onOpen={(id) => navigate(`/decisions/${encodeURIComponent(id)}`)}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}

      <DecisionModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        defaultOwner={currentAlias}
        goals={goals}
        workstreams={workstreams}
        tasks={tasks}
        services={services}
        documents={documents}
        serviceTitles={serviceTitles}
        storyTitles={storyTitles}
        onSubmit={onSubmit}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        title="Remove decision?"
        confirmLabel="Remove decision"
        loading={deleting}
        onConfirm={() => void confirmDelete()}
        onClose={() => setPendingDelete(null)}
        message="Remove this decision from the log? This can't be undone."
      />
    </div>
  );
}

export default DecisionsPage;
